/**
 * insertTextChild — standalone block-child TEXT / MTEXT → `text-run` for the
 * PDF plot INSERT converter (PDF-TEXT-CHILD-GEOMETRY, deferred out of #507).
 *
 * Split out of `insertConverter.ts` (CLAUDE.md file-size guardrail — the host
 * module is already >1000 lines after #507's ATTDEF/ATTRIB OCS work), sibling
 * to `insertAttributes.ts`'s ATTDEF/ATTRIB emission. `textChildToRun` rides
 * the SAME OCS-aware mirror-parity scalar-rotation composition
 * (`InsertWalkState.rotationSumDeg` / `scaleXNeg` / `scaleYNeg`, folded via
 * `insertAttributes.ts foldScalarRotationDeg`, ADD-vs-REFLECT per
 * `insertConverter.ts composeOcsRotationDeg` / `ocsMirrorParity`) that
 * `insertAttributes.ts resolveAttrPlacement` already applies to block-local
 * ATTDEF / ATTRIB text (@codex #507) — no new composition mechanism.
 *
 * Two entangled gaps closed here (both verified against the typed reference
 * oracle `scene3d/adapters/typed/blockTransform.ts`'s `Text` / `MText` cases,
 * the SAME oracle `insertAttributes.ts` cites throughout):
 *
 *   1. Prior code took `height` / `rotation` / `widthFactor` VERBATIM off the
 *      child — dropping the enclosing INSERT's scale / rotation / OCS-mirror
 *      entirely (a block-nested TEXT rendered at its AUTHORED size/angle no
 *      matter how the owning INSERT scaled or rotated it). Fix: `height ·
 *      |scaleY|`, `rotation + scalarRotationDeg` (mirror-aware fold), `Text`
 *      `widthFactor · |scaleX|/|scaleY|` — the exact oracle formulas
 *      (`blockTransform.ts transformBlockChild`'s `Text`/`MText` cases),
 *      already pinned for ATTDEF/ATTRIB by `insertConverter.attrib.test.ts`
 *      PDF-F8/F9/F10.
 *   2. MTEXT's OWN authored `rotation` (DXF group 50) was hardcoded to `0`
 *      unconditionally — the oracle documents this as a "deliberate
 *      divergence from legacy" (`blockTransform.ts` module header: "legacy
 *      DROPS the child's own rotation ... a verified legacy bug; the typed
 *      path sums like Text"). Fix: MTEXT sums `child.rotation +
 *      scalarRotationDeg` like TEXT, UNLESS it carries a CUSTOM
 *      `xAxisDirection` (DXF group 11) — per the DXF reference + ezdxf
 *      `MText.get_rotation()`, a custom direction vector takes PRECEDENCE
 *      over `rotation` (`command-engine/geometryTransforms.ts
 *      effectiveMTextAngleDeg`), so its WORLD angle is read off the vector
 *      mapped through the INSERT's linear part instead — a direct vector
 *      transform sidesteps the mirror-decomposition ambiguity
 *      `foldScalarRotationDeg`'s module header documents (a composed
 *      single-axis mirror is genuinely ambiguous for a SCALAR angle sum, but
 *      not for an actual direction VECTOR).
 *
 * MTEXT multi-run rich formatting (inline codes / paragraph wrap / stacked
 * lines) stays OUT of scope here — `insertConverter.ts`'s module header
 * already documents block-child MTEXT as "recorded as a single run,
 * matching PDF-1 behaviour"; that full-layout gap is the separate PDF-2b
 * item this slice does not touch.
 */

import { isCustomXAxisDirection } from "@dxf-ai-editor/command-engine";
import { coerceColor, colorToLegacyString } from "@dxf-ai-editor/drawing-model";
import type { BlockChildEntity } from "@dxf-ai-editor/drawing-model";

import { resolvePlotRgb } from "../pdfTransform";
import type { CadConvertContext, CadPrimitive } from "../cadPrimitive";
import { apply, applyLinear, type Affine2D } from "./affine2d";
import {
  affineScaleX,
  affineScaleY,
  foldScalarRotationDeg,
  resolveChildLayer,
} from "./insertAttributes";

/**
 * The subset of `insertConverter.ts InsertWalkState` `textChildToRun` reads —
 * structural twin of `insertAttributes.ts AttrEmitContext` (same rationale:
 * this leaf never imports its only consumer, dependency-cruiser zero-cycle
 * gate; CLAUDE.md ПРИОРИТЕТ #2). Unlike `AttrEmitContext`, this context
 * carries `matrix` directly rather than a separate `baseMatrix` parameter — a
 * standalone block-child TEXT/MTEXT has no WORLD `overridePosition` concept
 * (always block-local; `state.matrix` at the leaf IS the per-level
 * `baseMatrix` `walkBlock` already composed), so there is no outer-residual
 * branch that needs `matrix` kept out of the shared context for.
 */
export interface TextChildEmitContext {
  /** Conversion context — used only for its `layerColors` here. */
  ctx: CadConvertContext;
  /** Composed local→world affine at this nesting level (block base already
   *  subtracted — `walkBlock`'s `baseMatrix`). */
  matrix: Affine2D;
  /** Owning top-level INSERT id (stamped as the text-run's `entityId`). */
  parentId: string;
  /** The owning INSERT's OWN resolved colour hex — BYBLOCK children inherit
   *  it (a block-child TEXT/MTEXT with an explicit colour ignores this). */
  blockColor: string | null;
  /** The owning INSERT's resolved OCG layer — a layer-"0" child inherits it. */
  insertLayer: string;
  /**
   * PDF-F10 twin — the accumulated AUTHORED INSERT-rotation SUM (deg) for the
   * block-local scalar rotation, carried from the walk (NOT inferred from the
   * composed matrix — a composed single-axis mirror is ambiguous; see
   * `insertAttributes.ts foldScalarRotationDeg`). Already folds the OCS-lift
   * scalar contribution + mirror-parity REFLECT/ADD decision
   * (`insertConverter.ts composeOcsRotationDeg`).
   */
  rotationSumDeg: number;
  /** PDF-F10 twin — sign of the accumulated authored scaleX product. */
  scaleXNeg: boolean;
  /** PDF-F10 twin — sign of the accumulated authored scaleY product. */
  scaleYNeg: boolean;
}

/**
 * Resolve the WORLD rotation (degrees, CCW) a block-child MTEXT's glyph run
 * takes. A CUSTOM `xAxisDirection` (DXF group 11 — genuinely non-default;
 * `isCustomXAxisDirection` per `command-engine/geometryTransforms.ts`) takes
 * PRECEDENCE over the authored `rotation` scalar (DXF reference + ezdxf
 * `MText.get_rotation()` / `effectiveMTextAngleDeg`): its WORLD angle comes
 * from mapping the direction VECTOR through the composed matrix's LINEAR part
 * (`applyLinear`) and reading `atan2` — an EXACT, unambiguous read for any
 * rotation/scale/mirror/OCS combination (no mirror-decomposition ambiguity;
 * that ambiguity is specific to composing a bare SCALAR angle, see
 * `foldScalarRotationDeg`'s module header). A default `{1,0,0}` direction
 * (the overwhelming common case, and the ONLY form `rotation`-driven MTEXT
 * ever carries) instead sums the scalar like TEXT — the oracle's "typed path
 * sums like Text" convention (`blockTransform.ts` module header).
 */
function mtextRotationDeg(
  child: Extract<BlockChildEntity, { type: "MText" }>,
  matrix: Affine2D,
  scalarRotationDeg: number,
): number {
  if (isCustomXAxisDirection(child.xAxisDirection)) {
    const worldDir = applyLinear(matrix, {
      x: child.xAxisDirection.x,
      y: child.xAxisDirection.y,
    });
    return (Math.atan2(worldDir.y, worldDir.x) * 180) / Math.PI;
  }
  return child.rotation + scalarRotationDeg;
}

/**
 * Convert a block-child TEXT / MTEXT to a recorded text run (PDF-2 renders).
 * Height / rotation / (TEXT-only) widthFactor ride the SAME block-local
 * scalar transform `insertAttributes.ts resolveAttrPlacement` applies to
 * block-local ATTDEF / ATTRIB text — see module header.
 */
export function textChildToRun(
  child: Extract<BlockChildEntity, { type: "Text" | "MText" }>,
  state: TextChildEmitContext,
): CadPrimitive {
  const insertion =
    child.type === "Text" ? child.insertionPoint : child.insertionPoint;
  const pos = apply(state.matrix, { x: insertion.x, y: insertion.y });
  const heightBase = child.type === "Text" ? child.height : child.nominalHeight;
  const scaleY = affineScaleY(state.matrix);
  const scaleX = affineScaleX(state.matrix);
  const scalarRotationDeg = foldScalarRotationDeg(
    state.rotationSumDeg,
    state.scaleXNeg,
    state.scaleYNeg,
  );
  const rotation =
    child.type === "Text"
      ? child.rotation + scalarRotationDeg
      : mtextRotationDeg(child, state.matrix, scalarRotationDeg);
  // PDF-F9 twin — glyph-aspect multiplier, guarded against a degenerate zero
  // scaleY (a 0-height text renders nothing anyway) so widthFactor never
  // carries a NaN/Infinity. MTEXT has no glyph widthFactor field on the model
  // (`mtextFromEntity` renders width factor 1, per the oracle's own
  // documented limitation — only its `referenceWidth` wrapping box could
  // carry the X scale, which this single-run PDF-1 record does not model),
  // so it keeps the prior verbatim `1` untouched rather than inventing a
  // scaled value for a field MTEXT does not semantically have.
  const widthFactorScale = scaleY === 0 ? 1 : scaleX / scaleY;
  // EPIC-COLOR-UNION S1 — project the typed union to the legacy plot string.
  const color = resolvePlotRgb(
    colorToLegacyString(coerceColor(child.color)),
    child.layer,
    state.ctx.layerColors,
    state.blockColor,
  );
  return {
    type: "text-run",
    entityId: state.parentId,
    // PDF-6.2a: block-child TEXT/MText follows the same layer-0 rule as
    // geometry children (own layer, or the INSERT's when on "0").
    layer: resolveChildLayer(child.layer, state.insertLayer),
    position: pos,
    height: heightBase * scaleY,
    rotation,
    widthFactor:
      child.type === "Text" ? child.widthFactor * widthFactorScale : 1,
    obliqueAngle: child.type === "Text" ? child.obliqueAngle : 0,
    hJustification: child.type === "Text" ? child.hJustification : 0,
    vJustification: child.type === "Text" ? child.vJustification : 0,
    styleName: child.styleName ?? "Standard",
    value: child.contents,
    color,
  };
}
